import React, { useEffect, useState } from 'react';
import { taskTimeAPI, userAPI, projectAPI } from '../services/api';
import Layout from '../components/Layout';
import Avatar from '../components/Avatar';
import useAuthStore from '../store/authStore';
import './OperationsHeadTimeTracking.css';

const Icon = ({ name, size = 20 }) => {
    const icons = {
        clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
        users: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
        activity: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>,
        briefcase: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>,
        trendingUp: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></svg>,
        filter: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>,
        clipboard: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" ry="1" /><path d="M9 12h6" /><path d="M9 16h6" /></svg>
    };
    return icons[name] || null;
};

const OperationsHeadTimeTracking = () => {
    const { user } = useAuthStore();
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [teamMembers, setTeamMembers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [activeTab, setActiveTab] = useState('active');

    // Filter State
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        projectId: '',
        userId: '',
        role: ''
    });

    const userRoles = ['Developer', 'QA Engineer', 'UI Engineer', 'Backend Developer', 'Full Stack Developer', 'Project Lead', 'Project Manager'];

    useEffect(() => {
        loadInitialData();
        loadAnalytics();

        // Auto-refresh every 60 seconds
        const interval = setInterval(() => {
            loadAnalytics();
        }, 60000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        loadAnalytics();
    }, [filters]);

    const loadInitialData = async () => {
        try {
            const [usersRes, projectsRes] = await Promise.all([
                userAPI.getAll(),
                projectAPI.getAll()
            ]);
            setTeamMembers(usersRes.data.data || []);
            setProjects(projectsRes.data.data || []);
        } catch (error) {
            console.error('Failed to load filter data:', error);
        }
    };

    const loadAnalytics = async () => {
        try {
            const res = await taskTimeAPI.getOperationalAnalytics(filters);
            setAnalytics(res.data.data);
        } catch (error) {
            console.error('Failed to load operational time data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const formatHours = (hours) => hours ? parseFloat(hours).toFixed(1) + 'h' : '0.0h';

    const getUtilizationColor = (percent) => {
        if (percent > 100) return '#ef4444';
        if (percent > 85) return '#f59e0b';
        if (percent > 50) return '#6b7280';
        return '#10b981';
    };

    const getUtilizationStatus = (percent) => {
        if (percent > 100) return 'Overworked';
        if (percent > 85) return 'Optimal';
        if (percent < 30) return 'Underutilized';
        return 'Healthy';
    };

    if (loading && !analytics) {
        return (
            <Layout>
                <div className="ops-time-loading">
                    <div className="marshmallow-spinner"></div>
                    <p>Securing operational time insights...</p>
                </div>
            </Layout>
        );
    }

    const { summary, activeTasks, workload, projectDistribution } = analytics || {};

    return (
        <Layout>
            <div className="ops-time-dash">
                {/* User Top Bar */}
                <div className="ops-top-info">
                    <div className="user-bread">
                        <span className="name">{user?.name}</span>
                        <span className="role-tag">{user?.role}</span>
                    </div>
                </div>

                {/* Header Section */}
                <header className="ops-time-header">
                    <div className="header-content">
                        <h1>Operational Time Analytics</h1>
                        <p>Team performance, capacity monitoring & delivery risks</p>
                    </div>
                    <div className="analytics-filters">
                        <select name="projectId" value={filters.projectId} onChange={handleFilterChange} className="filter-select">
                            <option value="">All Projects</option>
                            {projects.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                        </select>
                        <select name="userId" value={filters.userId} onChange={handleFilterChange} className="filter-select">
                            <option value="">All Employees</option>
                            {teamMembers.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
                        </select>
                        <select name="role" value={filters.role} onChange={handleFilterChange} className="filter-select">
                            <option value="">All Roles</option>
                            {userRoles.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                        <div className="date-group">
                            <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} className="filter-date" />
                            <span className="to">to</span>
                            <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} className="filter-date" />
                        </div>
                        <button className="refresh-btn" onClick={loadAnalytics}>
                            <Icon name="activity" size={16} />
                        </button>
                    </div>
                </header>

                {/* KPI Cards */}
                <div className="ops-time-metrics">
                    <div className="metric-card primary">
                        <div className="metric-icon"><Icon name="clock" size={24} /></div>
                        <div className="metric-content">
                            <div className="metric-value">{formatHours(summary?.totalHoursToday)}</div>
                            <div className="metric-label">Team Hours Today</div>
                        </div>
                    </div>
                    <div className="metric-card secondary">
                        <div className="metric-icon"><Icon name="trendingUp" size={24} /></div>
                        <div className="metric-content">
                            <div className="metric-value">{formatHours(summary?.totalHoursPeriod)}</div>
                            <div className="metric-label">Total Hours (Selected)</div>
                        </div>
                    </div>
                    <div className="metric-card success">
                        <div className="metric-icon"><Icon name="users" size={24} /></div>
                        <div className="metric-content">
                            <div className="metric-value">
                                {summary?.activeResourcesCount || 0}
                                <span className="idle-sub"> / {summary?.idleResourcesCount || 0} Idle</span>
                            </div>
                            <div className="metric-label">Active / Idle Resources</div>
                        </div>
                    </div>
                    <div className="metric-card info">
                        <div className="metric-icon"><Icon name="activity" size={24} /></div>
                        <div className="metric-content">
                            <div className="metric-value">{Math.round(summary?.teamUtilization || 0)}%</div>
                            <div className="metric-label">Team Utilization</div>
                        </div>
                    </div>
                </div>

                {/* Analytics Tabs */}
                <div className="ops-time-content">
                    <div className="ops-tabs-nav">
                        <button className={activeTab === 'active' ? 'active' : ''} onClick={() => setActiveTab('active')}>
                            <Icon name="activity" size={18} />
                            Live Activity
                        </button>
                        <button className={activeTab === 'workload' ? 'active' : ''} onClick={() => setActiveTab('workload')}>
                            <Icon name="users" size={18} />
                            Team Workload
                        </button>
                        <button className={activeTab === 'projects' ? 'active' : ''} onClick={() => setActiveTab('projects')}>
                            <Icon name="briefcase" size={18} />
                            Project Distribution
                        </button>
                    </div>

                    <div className="ops-tab-panel">
                        {activeTab === 'active' && (
                            <div className="active-tasks-view">
                                <div className="panel-header">
                                    <h3>Active Sessions Across Team</h3>
                                    <span className="live-status"><span className="dot"></span> Live updates</span>
                                </div>
                                <div className="active-tasks-list">
                                    {activeTasks?.length > 0 ? activeTasks.map(log => (
                                        <div key={log._id} className="active-log-card">
                                            <div className="user-profile">
                                                <Avatar name={log.user?.name} profileImage={log.user?.profileImage} size="small" />
                                                <div className="user-info">
                                                    <span className="user-name">{log.user?.name}</span>
                                                    <span className="user-role">{log.user?.role}</span>
                                                </div>
                                            </div>
                                            <div className="task-info">
                                                <span className="task-title">{log.task?.title}</span>
                                                <span className="project-name">{log.project?.name}</span>
                                            </div>
                                            <div className="duration-info">
                                                <span className="since-label">Started today at</span>
                                                <span className="start-time">{new Date(log.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="empty-state-container">
                                            <div className="empty-illustration">
                                                <div className="clipboard-icon">
                                                    <Icon name="clipboard" size={100} />
                                                </div>
                                                <div className="clock-badge">
                                                    <Icon name="clock" size={40} />
                                                </div>
                                            </div>
                                            <p className="empty-text">No active time logs in the team right now.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'workload' && (
                            <div className="workload-analysis-view">
                                <div className="panel-header">
                                    <h3>Weekly Resource Utilization</h3>
                                    <div className="legend">
                                        <span className="legend-item"><span className="color healthy"></span> Healthy</span>
                                        <span className="legend-item"><span className="color high"></span> High</span>
                                        <span className="legend-item"><span className="color over"></span> Overworked</span>
                                    </div>
                                </div>
                                <div className="workload-grid">
                                    {workload?.map(item => (
                                        <div key={item._id} className="workload-card">
                                            <div className="card-top">
                                                <span className="member-name">{item.name}</span>
                                                <span className={`status-tag ${getUtilizationStatus(item.utilization).toLowerCase()}`}>
                                                    {getUtilizationStatus(item.utilization)}
                                                </span>
                                            </div>
                                            <div className="hours-stat">
                                                <span className="val">{formatHours(item.hours)}</span>
                                                <span className="limit">of 40h limit</span>
                                            </div>
                                            <div className="util-bar">
                                                <div
                                                    className="fill"
                                                    style={{
                                                        width: `${Math.min(item.utilization, 100)}%`,
                                                        backgroundColor: getUtilizationColor(item.utilization)
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeTab === 'projects' && (
                            <div className="project-dist-view">
                                <div className="panel-header">
                                    <h3>Time Expenditure by Project</h3>
                                </div>
                                <div className="project-dist-list">
                                    <table className="ops-table">
                                        <thead>
                                            <tr>
                                                <th>Project Name</th>
                                                <th>Total Hours Logged</th>
                                                <th>Impact %</th>
                                                <th>Risk Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {projectDistribution?.map(p => (
                                                <tr key={p._id}>
                                                    <td><strong>{p.name}</strong></td>
                                                    <td>{formatHours(p.hours)}</td>
                                                    <td>
                                                        <div className="impact-bar">
                                                            <div className="bar" style={{ width: `${Math.min((p.hours / summary?.totalHoursWeek) * 100, 100)}%` }}></div>
                                                            <span>{Math.round((p.hours / (summary?.totalHoursWeek || 1)) * 100)}%</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="status-indicator low-risk">On Track</span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default OperationsHeadTimeTracking;
